import React from 'react';
import { Info } from 'lucide-react';
import { money, dollars, Delta } from './shared';

/* Grouped bars: lodged / approved / settled per period. Dashed line = lodgement target (months). */
function Bars({ items, testid }) {
  const max = Math.max(1, ...items.flatMap((i) => [i.lodged, i.approved, i.settled, i.lodge_target || 0]));
  const h = (v) => `${Math.max(v > 0 ? 2 : 0, (v / max) * 100)}%`;
  return (
    <div className="gpc-bars" data-testid={testid}>
      {items.map((it) => (
        <div className={`gpc-bar${it.current ? ' cur' : ''}`} key={it.key || it.from || it.label}>
          <div className="cols">
            {it.lodged > 0 && <span className="cap">{money(it.lodged)}</span>}
            {it.lodge_target > 0 && <span className="tgt" style={{ bottom: h(it.lodge_target) }} title={`Lodgement target ${money(it.lodge_target)}`} />}
            <div className="col l" style={{ height: h(it.lodged) }} title={`Lodged ${dollars(it.lodged)} · ${it.lodged_count} files`} />
            <div className="col a" style={{ height: h(it.approved) }} title={`Approved ${dollars(it.approved)} · ${it.approved_count} files`} />
            <div className="col s" style={{ height: h(it.settled) }} title={`Settled ${dollars(it.settled)} · ${it.settled_count} files`} />
          </div>
          <small>{it.label}{it.current ? ' ·' : ''}</small>
        </div>
      ))}
    </div>
  );
}

function LflTable({ rows, cols, testid }) {
  return (
    <table className="gpc-lfl" data-testid={testid}>
      <thead><tr><th>Line</th>{cols.map((c) => <th key={c}>{c}</th>)}</tr></thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.label} data-testid={`${testid}-${r.key}`}>
            <td>{r.label}</td>
            <td>{r.fmt(r.d.now)}<small>{r.d.now_count} files</small></td>
            <td>{r.fmt(r.d.prev_same_point)}<small>{r.d.prev_same_point_count} files</small></td>
            <td><Delta pct={r.d.delta_pct} /><small>{r.d.delta >= 0 ? '+' : '−'}{r.fmt(Math.abs(r.d.delta))}</small></td>
            {r.d.prev_full != null && <td>{r.fmt(r.d.prev_full)}<small>{r.d.vs_full_pct != null ? `now at ${r.d.vs_full_pct.toFixed(0)}% of it` : `${r.d.prev_full_count} files`}</small></td>}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/* Trends: last 8 weeks, last 6 months, and like-for-like comparisons at the same point in time. */
export default function TrendsPanel({ cc }) {
  const M = cc.like_for_like?.month || {};
  const W = cc.like_for_like?.week || {};
  const mRows = [
    { key: 'lodged', label: 'Lodged', d: M.lodged || {}, fmt: money },
    { key: 'approved', label: 'Approved', d: M.approved_amount || {}, fmt: money },
    { key: 'settled', label: 'Settled', d: M.settled || {}, fmt: money },
  ];
  const wRows = [
    { key: 'lodged', label: 'Lodged', d: W.lodged || {}, fmt: money },
    { key: 'approved', label: 'Approved', d: W.approved || {}, fmt: money },
    { key: 'settled', label: 'Settled', d: W.settled || {}, fmt: money },
  ];
  const improving = (M.lodged?.delta_pct ?? 0) >= 0;
  return (
    <div className="gpj-grid gpj-two" data-testid="gpc-trends">
      <section className="gpj-panel">
        <div className="gpj-panel-h"><h2>Last 8 weeks</h2><p>Mon–Sun by real file dates · current week is partial</p></div>
        <Bars items={cc.trend_weeks || []} testid="gpc-week-bars" />
        <div className="gpc-leg"><span><i style={{ background: 'var(--blue)' }} />Lodged</span><span><i style={{ background: 'var(--cyan)' }} />Approved</span><span><i style={{ background: 'var(--green)' }} />Settled</span></div>
        <div className="gpc-eyebrow" style={{ marginTop: 18 }}>This week vs last week <span>same weekdays (Mon → {W.weekday_label})</span></div>
        <LflTable rows={wRows} cols={['This week to date', 'Last week, same point', 'Change', 'Last week total']} testid="gpc-lfl-week" />
      </section>
      <section className="gpj-panel">
        <div className="gpj-panel-h"><h2>Last 6 months</h2><p>{improving ? 'Lodgements running ahead of last month at this point' : 'Lodgements running behind last month at this point'}</p></div>
        <Bars items={cc.trend_months || []} testid="gpc-month-bars" />
        <div className="gpc-leg"><span><i style={{ background: 'var(--blue)' }} />Lodged</span><span><i style={{ background: 'var(--cyan)' }} />Approved</span><span><i style={{ background: 'var(--green)' }} />Settled</span><span><i className="mark" style={{ width: 14, height: 0, borderTop: '2px dashed var(--fg)', background: 'none' }} />Lodgement target</span></div>
        <div className="gpc-eyebrow" style={{ marginTop: 18 }}>This month vs {M.prev_month} <span>like-for-like · first {M.bd_done} business days of each month</span></div>
        <LflTable rows={mRows} cols={['Month to date', `${M.prev_month}, same point`, 'Change', `${M.prev_month} full month`]} testid="gpc-lfl-month" />
        <div className="gpc-note"><Info /> Like-for-like compares the same number of elapsed business days, so a mid-month read isn't penalised against a full month. The last column shows the previous completed month's total for longer-term growth.</div>
      </section>
    </div>
  );
}
